import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CloudUpload, MoreHorizontal, Users, PlayCircle, X, Download, Copy, ExternalLink } from "lucide-react";

import { AccountsApi, GroupsApi, ServersApi, RestoreApi, type AccountRow, type BulkRestoreItem } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatBytes, jalaliDate, jalaliDateTimeCompact, num } from "@/lib/utils";

const PAGE_SIZE = 50;
const ALL = "__all__";

/**
 * Status filter values:
 *   ALL          — no status filter
 *   "no-backup"  — only accounts that never produced a successful full
 *                  backup (lastFullAt IS NULL). Matches the visible
 *                  "آخرین فول" column rendering "—".
 *   "has-backup" — only accounts that have at least one valid snapshot.
 */
type StatusFilter = typeof ALL | "no-backup" | "has-backup";

export function AccountsPage() {
  const qc = useQueryClient();
  // Initialise the "commercial only" filter from the URL so the dashboard's
  // "کاربران ویژه" KPI can link straight to a pre-filtered list
  // (/accounts?commercial=1).
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [serverId, setServerId] = useState<string>(ALL);
  const [groupId, setGroupId] = useState<string>(ALL);
  const [status, setStatus] = useState<StatusFilter>(ALL);
  const [commercial, setCommercial] = useState<boolean>(
    () => searchParams.get("commercial") === "1" || searchParams.get("commercial") === "true",
  );
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Keep the URL in sync so the filter survives refresh / is shareable.
  const toggleCommercial = (on: boolean) => {
    setCommercial(on);
    setPage(0);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set("commercial", "1");
        else next.delete("commercial");
        return next;
      },
      { replace: true },
    );
  };

  const filters = useMemo(
    () => ({
      q: q || undefined,
      serverId: serverId !== ALL ? serverId : undefined,
      groupId: groupId !== ALL ? groupId : undefined,
      noBackup: status === "no-backup" || undefined,
      hasSnapshots: status === "has-backup" || undefined,
      commercial: commercial || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [q, serverId, groupId, status, commercial, page],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["accounts", filters],
    queryFn: () => AccountsApi.list(filters),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  const servers = useQuery({ queryKey: ["servers-picker"], queryFn: () => ServersApi.list() });
  const groups = useQuery({ queryKey: ["groups"], queryFn: () => GroupsApi.list() });

  const rows = data?.items ?? [];
  const pageIds = rows.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = pageIds.some((id) => selected.has(id));

  const togglePage = (on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) pageIds.forEach((id) => next.add(id));
      else pageIds.forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleOne = (id: string, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const bulk = useMutation({
    mutationFn: (kind: "account_full" | "account_db" | "account_meta") =>
      AccountsApi.bulkBackup(Array.from(selected), kind),
    onSuccess: (r) => {
      toast.success(`${num(r.queued)} جاب به صف رفت`, {
        description: r.failed > 0 ? `${num(r.failed)} مورد ناموفق` : undefined,
      });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setSelected(new Set());
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  // Bulk download-link generation. The backend creates one
  // `download_link` RestoreRequest per selected account using each
  // account's latest valid snapshots. Failures are per-row (e.g.
  // "no valid snapshots") and surfaced in the result dialog so the
  // operator can copy the URLs that did succeed without losing them.
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);
  const [bulkRestoreResults, setBulkRestoreResults] = useState<BulkRestoreItem[]>([]);
  const bulkRestore = useMutation({
    mutationFn: () => RestoreApi.bulkCreate(Array.from(selected)),
    onSuccess: (r) => {
      setBulkRestoreResults(r.results);
      setBulkRestoreOpen(true);
      toast.success(`${num(r.succeeded)} لینک ساخته شد`, {
        description: r.failed > 0 ? `${num(r.failed)} مورد ناموفق — جزئیات در پنجره` : undefined,
      });
      qc.invalidateQueries({ queryKey: ["restore-list"] });
      setSelected(new Set());
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  return (
    <div className="space-y-5">
      {/* Title row — own line so the filter strip below gets the full
          width and never has to wrap onto two rows. Previously the title
          and 3 filters shared one wrap-flex row, which broke onto two
          lines on the typical operator desktop (1440px with the sidebar
          open). */}
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Users className="size-5 text-primary" />
          {fa.accounts.title}
        </h1>
        <p className="text-sm text-muted-foreground">اکانت‌های cPanel کشف‌شده در همه‌ی سرورها</p>
      </div>

      {/* Filter strip — single horizontal line. Search comes FIRST in the
          DOM so under RTL flow it lands on the right (the natural reading
          start for Persian operators). Selects then occupy the remaining
          space toward the left. `flex-wrap` is intentionally off; on
          narrow viewports the search shrinks via `min-w-0` instead of
          forcing the selects to a second row. */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder={fa.common.search}
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="flex-1 min-w-[12rem] h-9"
        />
        <Select value={serverId} onValueChange={(v) => { setServerId(v); setPage(0); }}>
          <SelectTrigger className="flex-1 min-w-[8rem] sm:flex-none sm:w-40 h-9"><SelectValue placeholder="سرور" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>همهٔ سرورها</SelectItem>
            {(servers.data?.items ?? []).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={groupId} onValueChange={(v) => { setGroupId(v); setPage(0); }}>
          <SelectTrigger className="flex-1 min-w-[8rem] sm:flex-none sm:w-40 h-9"><SelectValue placeholder="گروه" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>همهٔ گروه‌ها</SelectItem>
            {(groups.data ?? []).map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => { setStatus(v as StatusFilter); setPage(0); }}
        >
          <SelectTrigger className="flex-1 min-w-[8rem] sm:flex-none sm:w-40 h-9">
            <SelectValue placeholder="وضعیت" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>همهٔ وضعیت‌ها</SelectItem>
            <SelectItem value="no-backup">بدون بکاپ</SelectItem>
            <SelectItem value="has-backup">دارای بکاپ</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={commercial ? "default" : "outline"}
          size="sm"
          className="h-9 shrink-0 gap-1.5"
          onClick={() => toggleCommercial(!commercial)}
          title="نمایش فقط کاربران ویژه (تجاری)"
        >
          {commercial && <X className="size-3.5" />}
          کاربران ویژه
        </Button>
      </div>

      {selected.size > 0 && (
        <Card className="border-primary/60 bg-primary/5">
          <CardContent className="py-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">
              {num(selected.size)} اکانت انتخاب شده
            </span>
            <div className="ms-auto flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => bulk.mutate("account_full")}
                disabled={bulk.isPending}
              >
                <PlayCircle className="size-4" />
                Full backup
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulk.mutate("account_db")}
                disabled={bulk.isPending}
              >
                <PlayCircle className="size-4" />
                DB only
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulk.mutate("account_meta")}
                disabled={bulk.isPending}
              >
                <PlayCircle className="size-4" />
                Meta only
              </Button>
              {/* Visual divider between "create new backups" and
                  "create download links from existing backups". They
                  are different operations the operator should not
                  fat-finger. */}
              <span className="h-5 w-px bg-border" />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => bulkRestore.mutate()}
                disabled={bulkRestore.isPending}
                title="ساخت لینک دانلود برای همه‌ی انتخاب‌شده‌ها از آخرین snapshot معتبر"
              >
                <Download className="size-4" />
                {bulkRestore.isPending ? "در حال ساخت…" : "بازیابی دسته‌جمعی"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
              >
                <X className="size-4" />
                لغو انتخاب
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            مجموع: <span className="text-foreground">{num(data?.total ?? 0)}</span> اکانت
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : isError ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.error}</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.empty}</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          allPageSelected ? true : someSelected ? "indeterminate" : false
                        }
                        onCheckedChange={(v) => togglePage(Boolean(v))}
                        aria-label="انتخاب همه"
                      />
                    </TableHead>
                    <TableHead>{fa.accounts.user}</TableHead>
                    <TableHead>{fa.accounts.domain}</TableHead>
                    <TableHead>{fa.accounts.server}</TableHead>
                    <TableHead>{fa.accounts.group}</TableHead>
                    <TableHead className="text-center">{fa.accounts.sizeOnDisk}</TableHead>
                    <TableHead>{fa.accounts.lastFull}</TableHead>
                    <TableHead>{fa.accounts.lastDb}</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((a) => (
                    <AccountItem
                      key={a.id}
                      acc={a}
                      selected={selected.has(a.id)}
                      onSelect={(v) => toggleOne(a.id, v)}
                    />
                  ))}
                </TableBody>
              </Table>

              {(data?.total ?? 0) > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-3">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    قبلی
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    صفحه {num(page + 1)} از {num(Math.ceil((data?.total ?? 0) / PAGE_SIZE))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * PAGE_SIZE >= (data?.total ?? 0)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    بعدی
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <BulkRestoreResultDialog
        open={bulkRestoreOpen}
        onOpenChange={setBulkRestoreOpen}
        results={bulkRestoreResults}
      />
    </div>
  );
}

/**
 * Modal that lists each account in the bulk-restore submission with
 * either its generated download URL (success) or the per-row error
 * (failure). The bulk-create endpoint returns immediately with the
 * RestoreRequest IDs, but the actual download URL only becomes
 * available once the engine finishes building cpmove + uploading to
 * offback (a few seconds per account). We poll `/restore?accountId=`
 * every 3 seconds to populate the URL column live as each row
 * transitions to `ready`. The user gets visible "آماده‌سازی…" badges
 * that flip to download URLs without having to refresh, plus the
 * "کپی همه" button stays correct because it reads from current state.
 *
 * The URL we get from the API is already absolute when the artifact
 * is hosted on offback (e.g. https://offback.mizbanfadevops.com/r/...)
 * and relative for legacy panel-served paths — we render the absolute
 * one verbatim and prefix relatives with `window.location.origin`.
 *
 * "Copy all URLs" copies one-per-line so the operator can paste into
 * an internal ticket / Telegram / email without manually clicking 50
 * copy buttons.
 */
function BulkRestoreResultDialog({
  open,
  onOpenChange,
  results,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  results: BulkRestoreItem[];
}) {
  const succeeded = results.filter((r): r is Extract<BulkRestoreItem, { ok: true }> => r.ok);
  const failed = results.filter((r) => !r.ok);

  // Poll every 3s until every successful row has hit a terminal
  // state (ready/failed/expired/revoked). React-Query handles the
  // "stop polling once stable" via refetchInterval returning false.
  const restorePoll = useQuery({
    enabled: open && succeeded.length > 0,
    queryKey: ["bulk-restore-poll", succeeded.map((s) => s.restoreId).sort().join(",")],
    queryFn: async () => {
      const rows = await Promise.all(succeeded.map((s) => RestoreApi.get(s.restoreId).catch(() => null)));
      const map = new Map<string, { state: string; downloadUrl: string | null }>();
      for (let i = 0; i < succeeded.length; i++) {
        const row = rows[i];
        if (row) map.set(succeeded[i]!.restoreId, { state: row.state, downloadUrl: row.downloadUrl });
      }
      return map;
    },
    refetchInterval: (q) => {
      const m = q.state.data;
      if (!m) return 3000;
      const allTerminal = succeeded.every((s) => {
        const v = m.get(s.restoreId);
        return v && ["ready", "failed", "expired", "revoked"].includes(v.state);
      });
      return allTerminal ? false : 3000;
    },
  });

  const buildUrl = (it: BulkRestoreItem) => {
    if (!it.ok) return null;
    const polled = restorePoll.data?.get(it.restoreId);
    const url = polled?.downloadUrl ?? null;
    if (!url) return null;
    return url.startsWith("http") ? url : `${window.location.origin}${url}`;
  };

  const stateOf = (it: BulkRestoreItem): string | null => {
    if (!it.ok) return null;
    return restorePoll.data?.get(it.restoreId)?.state ?? "preparing";
  };

  // Count rows that have actually finished building + uploading;
  // succeeded.length is "submission accepted" which can be ahead of
  // "URL is ready". Used to gate the copy-all button so the operator
  // doesn't get a partial list while half the offback uploads are
  // still in flight.
  const readyUrls = succeeded
    .map((r) => ({ user: r.cpanelUser ?? r.accountId, url: buildUrl(r) }))
    .filter((x): x is { user: string; url: string } => Boolean(x.url));

  const copyAll = async () => {
    if (readyUrls.length === 0) return;
    const lines = readyUrls.map((x) => `${x.user}\t${x.url}`).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success(`${num(readyUrls.length)} لینک کپی شد`);
    } catch (e) {
      toast.error("کپی نشد", { description: e instanceof Error ? e.message : String(e) });
    }
  };

  if (!open) return null;
  return (
    // Plain overlay instead of pulling in Radix Dialog — keeps the
    // extra component tree out of the table page bundle. The
    // backdrop click + Esc handlers are minimal but enough for an
    // operator-facing modal that's only ever triggered by an
    // explicit action.
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.currentTarget === e.target) onOpenChange(false);
      }}
    >
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">نتیجه‌ی بازیابی دسته‌جمعی</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {num(readyUrls.length)} از {num(succeeded.length)} آماده
              {failed.length > 0 && <span className="text-destructive"> · {num(failed.length)} ناموفق</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {readyUrls.length > 0 && (
              <Button size="sm" variant="secondary" onClick={copyAll}>
                <Copy className="size-4" />
                کپی همه‌ی لینک‌ها ({num(readyUrls.length)})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="size-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>اکانت</TableHead>
                <TableHead>وضعیت</TableHead>
                <TableHead>لینک / خطا</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((r) => {
                const url = buildUrl(r);
                const st = stateOf(r);
                return (
                  <TableRow key={r.accountId}>
                    <TableCell className="font-medium lat-num text-sm">
                      {r.cpanelUser ?? r.accountId}
                    </TableCell>
                    <TableCell>
                      {!r.ok ? (
                        <Badge variant="destructive">ناموفق</Badge>
                      ) : st === "ready" ? (
                        <Badge variant="default">آماده</Badge>
                      ) : st === "failed" ? (
                        <Badge variant="destructive">خطا</Badge>
                      ) : (
                        <Badge variant="secondary">در حال ساخت…</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.ok && url ? (
                        <span className="lat-num font-mono text-[11px] break-all" dir="ltr">
                          {url}
                        </span>
                      ) : !r.ok ? (
                        <span className="text-destructive">{r.error}</span>
                      ) : (
                        <span className="text-muted-foreground">در حال آماده‌سازی…</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.ok && url && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            title="کپی"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(url);
                                toast.success("کپی شد");
                              } catch (e) {
                                toast.error("کپی نشد", {
                                  description: e instanceof Error ? e.message : String(e),
                                });
                              }
                            }}
                          >
                            <Copy className="size-3.5" />
                          </Button>
                          <Button asChild variant="ghost" size="icon" className="size-7" title="باز کردن">
                            <a href={url} target="_blank" rel="noreferrer">
                              <ExternalLink className="size-3.5" />
                            </a>
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function AccountItem({
  acc,
  selected,
  onSelect,
}: {
  acc: AccountRow;
  selected: boolean;
  onSelect: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const backup = useMutation({
    mutationFn: (kind: "account_full" | "account_db" | "account_meta") => AccountsApi.backupNow(acc.id, kind),
    onSuccess: (r) => {
      toast.success("جاب در صف قرار گرفت", { description: `Job ID: ${r.jobId}` });
      navigate(`/jobs/${r.jobId}`);
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40 transition-colors"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("[data-no-row-nav], button, [role=menu], [role=menuitem], [role=checkbox]")) return;
        navigate(`/accounts/${acc.id}`);
      }}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={(v) => onSelect(Boolean(v))}
          aria-label={`انتخاب ${acc.user}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium lat-num">{acc.user}</span>
          {acc.commercial && <Badge variant="default">{fa.accounts.commercial}</Badge>}
          {acc.suspended && <Badge variant="destructive">{fa.accounts.suspended}</Badge>}
          {acc.graceUntil && new Date(acc.graceUntil).getTime() > Date.now() && (
            <Badge variant="warning" className="lat-num whitespace-nowrap">
              {fa.accounts.grace} {jalaliDate(acc.graceUntil)}
            </Badge>
          )}
          {acc.excludeFromBackup && (
            <Badge variant="destructive" className="whitespace-nowrap shrink-0">
              بدون بکاپ
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="lat-num text-muted-foreground">
        <div className="leading-tight">
          <div>{acc.primaryDomain ?? "—"}</div>
          {acc.ownerEmail && (
            <div className="text-[11px] text-muted-foreground/70 truncate max-w-[220px]">{acc.ownerEmail}</div>
          )}
        </div>
      </TableCell>
      <TableCell>{acc.serverName}</TableCell>
      <TableCell>{acc.groupName ?? <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-center tabular">{acc.sizeBytes != null ? formatBytes(acc.sizeBytes) : "—"}</TableCell>
      {/* `dir="ltr"` on the date span is essential: in the page's RTL
          flow the bidi algorithm treats "1404/02/28" and "14:30" as two
          separate "European Number" runs and reverses their order, so
          the user ends up seeing `14:30 1404/02/28` (time on the left,
          date on the right) — the opposite of what reads naturally for
          this layout. Forcing the span to LTR keeps the source order
          intact: date on the left, time on the right. */}
      <TableCell className="text-muted-foreground">
        <div className="leading-tight">
          <div
            dir="ltr"
            className="whitespace-nowrap text-xs lat-num tabular text-end"
            title={acc.lastFullAt ?? undefined}
          >
            {jalaliDateTimeCompact(acc.lastFullAt)}
          </div>
          {acc.lastFullBytes != null && acc.lastFullBytes > 0 && (
            <div className="text-[11px] text-muted-foreground/70 tabular lat-num whitespace-nowrap">
              {formatBytes(acc.lastFullBytes)}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        <span
          dir="ltr"
          className="inline-block whitespace-nowrap text-xs lat-num tabular"
          title={acc.lastDbAt ?? undefined}
        >
          {jalaliDateTimeCompact(acc.lastDbAt)}
        </span>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="size-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{fa.accounts.backupNow}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => backup.mutate("account_full")}>
              <CloudUpload className="size-4" /> {fa.jobs.kinds.account_full}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => backup.mutate("account_db")}>
              <CloudUpload className="size-4" /> {fa.jobs.kinds.account_db}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => backup.mutate("account_meta")}>
              <CloudUpload className="size-4" /> {fa.jobs.kinds.account_meta}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
